/**
 * Unified time conversion utilities for office hours
 * Ensures consistent 12h ↔ 24h conversion across the application
 *
 * Uses minutes since midnight (0-1439) as the canonical internal representation
 * for all time calculations to avoid floating point precision issues.
 */

/**
 * Converts 12-hour format to 24-hour format
 * @param time12h - Time in 12-hour format (e.g., "8:00 AM", "12:30 PM")
 * @returns Time in 24-hour format (e.g., "08:00", "12:30")
 */
export const to24h = (time12h: string): string => {
  if (
    !time12h ||
    !/^\d{1,2}:\d{2}\s?(AM|PM)$/i.test(time12h.trim())
  ) {
    return '';
  }

  const trimmed = time12h.trim().toUpperCase();
  const ampmMatch = trimmed.match(/(AM|PM)$/);
  if (!ampmMatch) return '';

  const meridiem = ampmMatch[1];
  const timePart = trimmed.replace(/\s?(AM|PM)$/, '');
  const [hourStr, minuteStr] = timePart.split(':');

  const hour = Number(hourStr);
  const minute = Number(minuteStr);

  if (
    isNaN(hour) ||
    isNaN(minute) ||
    hour < 1 ||
    hour > 12 ||
    minute < 0 ||
    minute > 59
  ) {
    return '';
  }

  let hour24 = hour;
  if (meridiem === 'PM' && hour !== 12) {
    hour24 = hour + 12;
  } else if (meridiem === 'AM' && hour === 12) {
    hour24 = 0;
  }

  return `${hour24.toString().padStart(2, '0')}:${minute
    .toString()
    .padStart(2, '0')}`;
};

/**
 * Converts 24-hour format to 12-hour format
 * @param time24h - Time in 24-hour format (e.g., "08:00", "13:30")
 * @returns Time in 12-hour format (e.g., "8:00 AM", "1:30 PM")
 */
export const to12h = (time24h: string): string => {
  if (!time24h || !/^\d{2}:\d{2}$/.test(time24h)) {
    return '';
  }

  const [hour, minute] = time24h.split(':').map(Number);
  if (
    isNaN(hour) ||
    isNaN(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return '';
  }

  let hour12 = hour;
  let meridiem = 'AM';

  if (hour === 0) {
    hour12 = 12;
    meridiem = 'AM';
  } else if (hour === 12) {
    hour12 = 12;
    meridiem = 'PM';
  } else if (hour > 12) {
    hour12 = hour - 12;
    meridiem = 'PM';
  }

  return `${hour12}:${minute
    .toString()
    .padStart(2, '0')} ${meridiem}`;
};

/**
 * Normalizes a time string to 24-hour "HH:mm" format
 * @param time - Time string in various formats
 * @param isCloseTime - Whether this is a close time (affects 1-11 PM assumption)
 * @returns Normalized 24-hour time string
 */
export const normalizeTimeString = (
  time: string | null | undefined,
  isCloseTime?: boolean
): string | undefined => {
  if (!time) return undefined;

  // Handle 12-hour format with AM/PM (e.g., "2:30 PM", "12:00 AM")
  if (time.includes('AM') || time.includes('PM')) {
    const [timePart, meridiem] = time
      .toUpperCase()
      .split(/(?=AM|PM)/);
    const [h, m] = timePart.split(':');
    const hour = Number(h);
    const minute = Number(m);
    if (isNaN(hour) || isNaN(minute)) return undefined;

    let hour24 = hour;
    if (meridiem === 'PM' && hour !== 12) {
      hour24 = hour + 12;
    } else if (meridiem === 'AM' && hour === 12) {
      hour24 = 0;
    }

    return `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  }

  // Already in "HH:mm" format - validate it's actually valid
  if (/^\d{2}:\d{2}$/.test(time)) {
    const [h, m] = time.split(':');
    const hour = Number(h);
    const minute = Number(m);
    if (isNaN(hour) || isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      return undefined;
    }
    return time;
  }

  // "H:mm" or "HH:mm"
  if (/^\d{1,2}:\d{2}$/.test(time)) {
    const [h, m] = time.split(':');
    const hour = Number(h);
    const minute = Number(m);
    if (isNaN(hour) || isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) return undefined;

    // For close times, treat 1-11 as PM, 12 as noon, 0 as midnight
    if (isCloseTime) {
      if (hour === 0) return `00:${m}`;
      if (hour === 12) return `12:${m}`; // noon
      if (hour >= 1 && hour <= 11)
        return `${(hour + 12).toString().padStart(2, '0')}:${m}`;
      // 13-23 are already PM
      return `${hour.toString().padStart(2, '0')}:${m}`;
    } else {
      // For open times, treat 12 as noon, 0 as midnight, 1-11 as AM
      if (hour === 0) return `00:${m}`;
      if (hour === 12) return `12:${m}`; // noon
      return `${hour.toString().padStart(2, '0')}:${m}`;
    }
  }
  return undefined;
};

/**
 * Converts time to minutes since midnight for comparison
 * @param time - Time in HH:MM format
 * @returns Minutes since midnight (0-1439)
 */
export const timeToMinutes = (time: string): number => {
  if (!time) return 0;

  // Validate time string matches HH:MM format
  if (!/^\d{2}:\d{2}$/.test(time)) return 0;

  const [hoursStr, minutesStr] = time.split(':');
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);

  if (
    isNaN(hours) ||
    isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return 0;
  }

  return hours * 60 + minutes;
};

/**
 * Converts minutes since midnight back to HH:MM format
 * @param minutes - Minutes since midnight (0-1439)
 * @returns Time in HH:MM format
 */
export const minutesToTime = (minutes: number): string => {
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1439) {
    return '';
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Validates that a time string is in valid 24-hour format
 * @param time - Time string to validate
 * @returns True if valid 24-hour format
 */
export const isValid24hTime = (time: string): boolean => {
  if (!time) return false;
  if (!/^\d{2}:\d{2}$/.test(time)) return false;

  const [hoursStr, minutesStr] = time.split(':');
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);

  return !isNaN(hours) && !isNaN(minutes) &&
         hours >= 0 && hours <= 23 &&
         minutes >= 0 && minutes <= 59;
};

/**
 * Validates that a time string is in valid 12-hour format
 * @param time - Time string to validate
 * @returns True if valid 12-hour format
 */
export const isValid12hTime = (time: string): boolean => {
  if (!time) return false;
  if (!/^\d{1,2}:\d{2}\s?(AM|PM)$/i.test(time)) return false;

  const [timePart] = time.toUpperCase().split(/(?=AM|PM)/);
  const [h, m] = timePart.split(':').map(Number);

  return !isNaN(h) && !isNaN(m) &&
         h >= 1 && h <= 12 &&
         m >= 0 && m <= 59;
};

/**
 * Parse a time input string with various formats and return canonical 24h format
 * Supports partial inputs and auto-completion for better UX
 * @param input - User input string (e.g., "12:49", "12:49 pm", "1249", "12:49p")
 * @returns Object with parsed time in 24h format and validation status
 */
export const parseTimeInput = (input: string): { time: string; valid: boolean; error?: string } => {
  if (!input?.trim()) {
    return { time: '', valid: false, error: 'Time is required' };
  }

  const trimmed = input.trim().toLowerCase();

  // Handle already valid 24h format
  if (/^\d{2}:\d{2}$/.test(trimmed)) {
    const [h, m] = trimmed.split(':').map(Number);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return { time: trimmed, valid: true };
    }
    return { time: '', valid: false, error: 'Invalid time format' };
  }

  // Handle 12h format with AM/PM
  if (/^\d{1,2}:\d{2}\s?(am|pm)$/i.test(trimmed)) {
    const time24 = to24h(trimmed);
    if (time24) {
      return { time: time24, valid: true };
    }
    return { time: '', valid: false, error: 'Invalid time format' };
  }

  // Handle partial formats for better UX
  // "1249" -> "12:49"
  if (/^\d{3,4}$/.test(trimmed)) {
    const padded = trimmed.padStart(4, '0');
    const h = Number(padded.slice(0, 2));
    const m = Number(padded.slice(2, 4));

    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return { time: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`, valid: true };
    }
    return { time: '', valid: false, error: 'Invalid time format' };
  }

  // Handle "H:MM" format (single digit hour)
  if (/^\d{1}:\d{2}$/.test(trimmed)) {
    const [h, m] = trimmed.split(':').map(Number);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return { time: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`, valid: true };
    }
    return { time: '', valid: false, error: 'Invalid time format' };
  }

  return { time: '', valid: false, error: 'Unrecognized time format' };
};

/**
 * Generate time options for dropdown with minute precision
 * @param startHour - Starting hour (0-23)
 * @param endHour - Ending hour (0-23)
 * @param minuteStep - Minute increment (default: 1 for minute precision)
 * @returns Array of time options in 12h format
 */
export const generateTimeOptions = (startHour = 0, endHour = 23, minuteStep = 15): Array<{ label: string; value: string }> => {
  const options: Array<{ label: string; value: string }> = [];

  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 60; minute += minuteStep) {
      const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const time12 = to12h(time24);

      if (time12) {
        options.push({
          label: time12,
          value: time12
        });
      }
    }
  }

  return options;
};

/**
 * Normalize a time range to ensure proper ordering and format
 * @param startTime - Start time in 24h format
 * @param endTime - End time in 24h format
 * @returns Normalized time range or null if invalid
 */
export const normalizeTimeRange = (startTime: string, endTime: string): { start: string; end: string } | null => {
  if (!isValid24hTime(startTime) || !isValid24hTime(endTime)) {
    return null;
  }

  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);

  if (startMinutes >= endMinutes) {
    return null;
  }

  return { start: startTime, end: endTime };
};

/**
 * Unit tests for time conversion functions (dev only)
 */
if (import.meta.env.DEV) {
  // Test to24h function with edge cases
  console.assert(
    to24h('12:00 AM') === '00:00',
    'Midnight AM should be 00:00'
  );
  console.assert(
    to24h('12:00 PM') === '12:00',
    'Noon PM should be 12:00'
  );
  console.assert(
    to24h('12:49 AM') === '00:49',
    '12:49 AM should be 00:49'
  );
  console.assert(
    to24h('12:49 PM') === '12:49',
    '12:49 PM should be 12:49'
  );
  console.assert(
    to24h('1:30 PM') === '13:30',
    '1:30 PM should be 13:30'
  );
  console.assert(
    to24h('11:45 PM') === '23:45',
    '11:45 PM should be 23:45'
  );
  console.assert(
    to24h('9:15 AM') === '09:15',
    '9:15 AM should be 09:15'
  );

  // Test to12h function with edge cases
  console.assert(
    to12h('00:00') === '12:00 AM',
    '00:00 should be 12:00 AM'
  );
  console.assert(
    to12h('00:49') === '12:49 AM',
    '00:49 should be 12:49 AM'
  );
  console.assert(
    to12h('12:00') === '12:00 PM',
    '12:00 should be 12:00 PM'
  );
  console.assert(
    to12h('12:49') === '12:49 PM',
    '12:49 should be 12:49 PM'
  );
  console.assert(
    to12h('13:30') === '1:30 PM',
    '13:30 should be 1:30 PM'
  );
  console.assert(
    to12h('23:45') === '11:45 PM',
    '23:45 should be 11:45 PM'
  );
  console.assert(
    to12h('09:15') === '9:15 AM',
    '09:15 should be 9:15 AM'
  );

  // Test critical edge cases for TC-OH-009
  console.assert(
    to24h('11:59 AM') === '11:59',
    '11:59 AM should be 11:59'
  );
  console.assert(
    to24h('12:31 PM') === '12:31',
    '12:31 PM should be 12:31'
  );
  console.assert(
    to12h('11:59') === '11:59 AM',
    '11:59 should be 11:59 AM'
  );
  console.assert(
    to12h('12:31') === '12:31 PM',
    '12:31 should be 12:31 PM'
  );

  // Test round-trip conversion with critical times
  const testTimes = [
    '12:00 AM',
    '12:00 PM',
    '12:49 AM',
    '12:49 PM',
    '11:59 AM',
    '12:31 PM',
    '1:30 PM',
    '11:45 PM',
    '9:15 AM',
  ];
  testTimes.forEach((time) => {
    const converted = to24h(time);
    const backTo12h = to12h(converted);
    console.assert(
      backTo12h === time,
      `Round-trip conversion failed: ${time} -> ${converted} -> ${backTo12h}`
    );
  });

  // Test minutesToTime round-trip
  for (let minutes = 0; minutes <= 1439; minutes += 37) {
    // Test sampling
    const time = minutesToTime(minutes);
    const backToMinutes = timeToMinutes(time);
    console.assert(
      backToMinutes === minutes,
      `Minutes round-trip failed: ${minutes} -> ${time} -> ${backToMinutes}`
    );
  }

  // Console logging removed for production safety.
}
